import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import ContactUsButton from '../components/ContactUsButton'
import './PrivacyPage.css'

interface TocEntry {
  readonly id: string
  readonly key: string
}

const TOC: readonly TocEntry[] = [
  {id: 'summary', key: 'summary'},
  {id: 'controller', key: 'controller'},
  {id: 'what', key: 'what'},
  {id: 'why', key: 'why'},
  {id: 'sharing', key: 'sharing'},
  {id: 'where', key: 'where'},
  {id: 'security', key: 'security'},
  {id: 'retention', key: 'retention'},
  {id: 'rights', key: 'rights'},
  {id: 'age', key: 'age'},
  {id: 'cookies', key: 'cookies'},
  {id: 'changes', key: 'changes'},
  {id: 'contact', key: 'contact'},
]

const PROSE_COMPONENTS = {
  contact: <RouterLink to="/contact" />,
  cookies: <RouterLink to="/cookies" />,
  account: <RouterLink to="/account" />,
  refund: <RouterLink to="/refund" />,
  dpa: <a href="https://dataprotection.gov.sk" target="_blank" rel="noreferrer" />,
  strong: <strong />,
  em: <em />,
}

const pad2 = (n: number) => String(n).padStart(2, '0')

const PrivacyPage = () => {
  const {t} = useTranslation()

  return (
    <div className="privacy-root">
      <div className="pv-grid" aria-hidden />

      <main className="privacy-page">
        <header className="pv-hero">
          <span className="pv-eyebrow">
            <span className="pv-eyebrow-dot" />
            {t('privacy.eyebrow')}
          </span>
          <h1 className="pv-title">
            {t('privacy.titleLead')} <em>{t('privacy.titleEm')}</em>
          </h1>
          <p className="pv-lede">{t('privacy.lede')}</p>
          <div className="pv-meta">
            <span>{t('privacy.metaUpdated')}</span>
            <span className="pv-meta-sep" />
            <span>{t('privacy.metaReading')}</span>
            <span className="pv-meta-sep" />
            <span>{t('privacy.metaGoverned')}</span>
          </div>
        </header>

        <div className="pv-layout">
          <aside className="pv-toc" aria-label={t('privacy.tocLabel')}>
            <div className="pv-toc-label">{t('privacy.tocLabel')}</div>
            {TOC.map((entry, i) => (
              <a key={entry.id} href={`#${entry.id}`}>
                <span className="pv-toc-num">{pad2(i + 1)}</span>
                <span>{t(`privacy.toc.${entry.key}`)}</span>
              </a>
            ))}
          </aside>

          <article className="pv-prose">
            <section id="summary">
              <h2 data-num="01">{t('privacy.sections.summary.title')}</h2>
              <p>
                <Trans i18nKey="privacy.sections.summary.p1" components={PROSE_COMPONENTS} />
              </p>
              <div className="pv-callout">
                <span className="pv-callout-icon" aria-hidden>
                  i
                </span>
                <span>{t('privacy.sections.summary.callout')}</span>
              </div>
            </section>

            <section id="controller">
              <h2 data-num="02">{t('privacy.sections.controller.title')}</h2>
              <p>{t('privacy.sections.controller.p1')}</p>
              <div className="pv-address">
                <Trans
                  i18nKey="privacy.sections.controller.addressBody"
                  components={{br: <br />, strong: <strong />}}
                />
              </div>
              <p>
                <Trans i18nKey="privacy.sections.controller.emailLine" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="what">
              <h2 data-num="03">{t('privacy.sections.what.title')}</h2>
              <h3>{t('privacy.sections.what.account')}</h3>
              <ul>
                <li>{t('privacy.sections.what.accountBullets.identity')}</li>
                <li>{t('privacy.sections.what.accountBullets.avatar')}</li>
              </ul>
              <h3>{t('privacy.sections.what.project')}</h3>
              <ul>
                <li>{t('privacy.sections.what.projectBullets.meta')}</li>
                <li>{t('privacy.sections.what.projectBullets.uploads')}</li>
                <li>{t('privacy.sections.what.projectBullets.renders')}</li>
              </ul>
              <h3>{t('privacy.sections.what.billing')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="privacy.sections.what.billingBullets.orders" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.what.billingBullets.ledger" components={PROSE_COMPONENTS} />
                </li>
              </ul>
              <h3>{t('privacy.sections.what.usage')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="privacy.sections.what.usageBullets.analytics" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.what.usageBullets.errors" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.what.usageBullets.recaptcha" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="why">
              <h2 data-num="04">{t('privacy.sections.why.title')}</h2>
              <p>{t('privacy.sections.why.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="privacy.sections.why.bullets.contract" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.why.bullets.legitimate" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.why.bullets.legal" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.why.bullets.softOptIn" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.why.bullets.consent" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="sharing">
              <h2 data-num="05">{t('privacy.sections.sharing.title')}</h2>
              <p>{t('privacy.sections.sharing.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="privacy.sections.sharing.bullets.firebase" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.sharing.bullets.hetzner" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.sharing.bullets.lemonsqueezy" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.sharing.bullets.analytics" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.sharing.bullets.sentry" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.sharing.bullets.recaptcha" components={PROSE_COMPONENTS} />
                </li>
              </ul>
              <p>
                <Trans i18nKey="privacy.sections.sharing.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="where">
              <h2 data-num="06">{t('privacy.sections.where.title')}</h2>
              <p>
                <Trans i18nKey="privacy.sections.where.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="privacy.sections.where.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="security">
              <h2 data-num="07">{t('privacy.sections.security.title')}</h2>
              <p>{t('privacy.sections.security.p1')}</p>
              <ul>
                <li>{t('privacy.sections.security.bullets.transit')}</li>
                <li>{t('privacy.sections.security.bullets.rest')}</li>
                <li>{t('privacy.sections.security.bullets.access')}</li>
                <li>{t('privacy.sections.security.bullets.least')}</li>
                <li>{t('privacy.sections.security.bullets.breach')}</li>
              </ul>
            </section>

            <section id="retention">
              <h2 data-num="08">{t('privacy.sections.retention.title')}</h2>
              <ul>
                <li>
                  <Trans i18nKey="privacy.sections.retention.bullets.projects" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.retention.bullets.renders" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.retention.bullets.account" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.retention.bullets.billing" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.retention.bullets.optOut" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="rights">
              <h2 data-num="09">{t('privacy.sections.rights.title')}</h2>
              <p>{t('privacy.sections.rights.p1')}</p>
              <ul>
                <li>{t('privacy.sections.rights.bullets.access')}</li>
                <li>{t('privacy.sections.rights.bullets.correct')}</li>
                <li>{t('privacy.sections.rights.bullets.erase')}</li>
                <li>{t('privacy.sections.rights.bullets.restrict')}</li>
                <li>{t('privacy.sections.rights.bullets.portable')}</li>
                <li>{t('privacy.sections.rights.bullets.withdraw')}</li>
                <li>{t('privacy.sections.rights.bullets.optOut')}</li>
                <li>{t('privacy.sections.rights.bullets.automated')}</li>
                <li>
                  <Trans i18nKey="privacy.sections.rights.bullets.complain" components={PROSE_COMPONENTS} />
                </li>
              </ul>
              <p>
                <Trans i18nKey="privacy.sections.rights.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="age">
              <h2 data-num="10">{t('privacy.sections.age.title')}</h2>
              <p>{t('privacy.sections.age.p1')}</p>
            </section>

            <section id="cookies">
              <h2 data-num="11">{t('privacy.sections.cookies.title')}</h2>
              <p>{t('privacy.sections.cookies.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="privacy.sections.cookies.bullets.session" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.cookies.bullets.recaptcha" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="privacy.sections.cookies.bullets.analytics" components={PROSE_COMPONENTS} />
                </li>
              </ul>
              <p>
                <Trans i18nKey="privacy.sections.cookies.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="changes">
              <h2 data-num="12">{t('privacy.sections.changes.title')}</h2>
              <p>{t('privacy.sections.changes.p1')}</p>
            </section>

            <section id="contact">
              <h2 data-num="13">{t('privacy.sections.contact.title')}</h2>
              <p>{t('privacy.sections.contact.p1')}</p>
              <div className="pv-address">
                <Trans i18nKey="privacy.sections.contact.addressBody" components={{br: <br />, strong: <strong />}} />
              </div>
              <p>
                <Trans i18nKey="privacy.sections.contact.emailLine" components={PROSE_COMPONENTS} />
              </p>
              <div className="pv-contact-cta">
                <ContactUsButton />
              </div>
            </section>
          </article>
        </div>
      </main>
    </div>
  )
}

export default PrivacyPage
